import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles, Typography,
} from '@material-ui/core';
import DayPicker from 'react-day-picker';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import MomentLocaleUtils from 'react-day-picker/moment';
import 'moment/locale/fr';
import { useRecoilState, useRecoilValue } from 'recoil';
import CalendarWeekPickerNavBar from './CalendarWeekPickerNavBar';
import { availabilitiesPeriod } from '../../config/config';
import {
  selectedClinicAvailabilitiesFilteredState,
  selectedClinicAvailableDaysState, selectedDayjsState,
} from '../../state/selected';

const useStyles = makeStyles((theme) => ({
  weekPicker: {
    padding: theme.spacing(2),
    '& .DayPicker:not(.DayPicker--interactionDisabled) .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--outside):hover': {
      backgroundColor: 'transparent',
    },
    '& .DayPicker': {
      fontSize: 14,
      lineHeight: 1.1,
      width: '100%',
    },
    '& .DayPicker-day': {
      cursor: 'default',
    },
    '& .DayPicker-Month': {
      flex: 1,
      margin: 0,
    },
    '& .DayPicker-Caption': {
      padding: 0,
    },
    '& .DayPicker-Day--today': {
      color: theme.palette.text.primary,
    },
    '& .DayPicker-Day.DayPicker-Day--selectedRange, & .DayPicker-Day.DayPicker-Day--selectedRange:hover, & .DayPicker-Day.DayPicker-Day--hoverRange, & .DayPicker-Day.DayPicker-Day--hoverRange:hover': {
      backgroundColor: `${theme.palette.primary.light} !important`,
      color: theme.palette.text.primary,
      borderRadius: 0,
      cursor: 'pointer',
    },
    '& .DayPicker-Day.DayPicker-Day--selectedRange.DayPicker-Day--selectedRangeStart, & .DayPicker-Day.DayPicker-Day--hoverRange.DayPicker-Day--hoverRangeStart': {
      borderRadius: '50% 0 0 50%',
    },
    '& .DayPicker-Day.DayPicker-Day--selectedRange.DayPicker-Day--selectedRangeEnd, & .DayPicker-Day.DayPicker-Day--hoverRange.DayPicker-Day--hoverRangeEnd': {
      borderRadius: '0 50% 50% 0',
    },
    '& .DayPicker-Day .DayPicker-Day-text': {
      position: 'relative',
    },
    '& .DayPicker-Day .DayPicker-Day-text.DayPicker-Day-disabled': {
      textDecoration: 'line-through',
      opacity: 0.3,
    },
    '& .DayPicker-Day.DayPicker-Day--selectedRange.DayPicker-Day--selectedRangeStart, & .DayPicker-Day.DayPicker-Day--selectedRange.DayPicker-Day--selectedRangeEnd, & .DayPicker-Day.DayPicker-Day--hoverRange.DayPicker-Day--hoverRangeStart, & .DayPicker-Day.DayPicker-Day--hoverRange.DayPicker-Day--hoverRangeEnd': {
      position: 'relative',
      '&::before': {
        content: '\' \'',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: '100px',
        backgroundColor: theme.palette.primary.main,
      },
      '& .DayPicker-Day-text': {
        color: theme.palette.primary.contrastText,
      },
      '& .DayPicker-Day-text.DayPicker-Day-disabled': {
        textDecoration: 'line-through',
        opacity: 0.3,
      },
    },

  },
}));

const CalendarWeekPicker = (props) => {
  const classes = useStyles();
  const intl = useIntl();

  const [
    selectedDayjs, setSelectedDayjs,
  ] = useRecoilState(selectedDayjsState);
  const selectedClinicAvailableDays = useRecoilValue(selectedClinicAvailableDaysState);
  const selectedClinicAvailabilitiesFiltered = useRecoilValue(
    selectedClinicAvailabilitiesFilteredState,
  );

  const today = dayjs();

  const {
    callbackSelection,
  } = props;

  const [hoverRange, setHoverRange] = useState(null);

  const getWeekDays = (weekStart) => {
    const days = [weekStart];
    for (let i = 1; i < 7; i += 1) {
      days.push(
        dayjs(weekStart)
          .add(i, 'days')
          .toDate(),
      );
    }
    return days;
  };

  const isWeekDisabled = (date) => {
    const dateDayjs = dayjs(date);
    let disabled = true;
    const weekDays = Array(5)
      .fill(0)
      .map((day, i) => dateDayjs.day(i + 1)
        .format('YYYY-MM-DD'));

    weekDays.map((day) => {
      if (disabled && selectedClinicAvailableDays.includes(day)) {
        disabled = false;
      }
      return null;
    });
    return disabled;
  };

  const getWeekRange = (date) => {
    const refHoverDate = dayjs(date)
      .subtract(1, 'days');

    return {
      from: refHoverDate
        .day(1)
        .toDate(),
      to: refHoverDate
        .day(7)
        .toDate(),
    };
  };

  const selectedDays = getWeekDays(getWeekRange(selectedDayjs).from);

  const daysAreSelected = selectedDays.length > 0;

  const modifiers = {
    hoverRange,
    selectedRange: daysAreSelected && {
      from: selectedDays[0],
      to: selectedDays[6],
    },
    hoverRangeStart: hoverRange && hoverRange.from,
    hoverRangeEnd: hoverRange && hoverRange.to,
    selectedRangeStart: daysAreSelected && selectedDays[0],
    selectedRangeEnd: daysAreSelected && selectedDays[6],
  };

  const handleDayEnter = (date) => {
    if (!isWeekDisabled(date)) {
      setHoverRange(getWeekRange(date));
    }
  };

  const handleDayLeave = () => {
    setHoverRange(null);
  };

  const handleDayChange = (date) => {
    if (!isWeekDisabled(date)) {
      setSelectedDayjs(dayjs(date));
      return callbackSelection();
    }
    return null;
  };

  const renderDay = (day) => {
    const date = day.getDate();
    const dateDayjs = dayjs(day);
    let classNames = '';

    if (selectedClinicAvailabilitiesFiltered.filter((av) => av.date === dateDayjs.format('YYYY-MM-DD')).length < 1) {
      classNames = classNames.concat(' DayPicker-Day-disabled');
    }

    return <span className={`DayPicker-Day-text${classNames}`}>{date}</span>;
  };

  return (
    <div className={classes.weekPicker}>
      <Typography component="div" variant="subtitle1">
        <DayPicker
          month={selectedDayjs.toDate()}
          selectedDays={selectedDays}
          showOutsideDays
          modifiers={modifiers}
          onDayClick={handleDayChange}
          onDayMouseEnter={handleDayEnter}
          onDayMouseLeave={handleDayLeave}
          renderDay={renderDay}
          firstDayOfWeek={1}
          localeUtils={MomentLocaleUtils}
          locale={intl.locale}
          navbarElement={CalendarWeekPickerNavBar}
          fromMonth={today.toDate()}
          toMonth={today.add(availabilitiesPeriod.nb, availabilitiesPeriod.unit)
            .toDate()}
        />
      </Typography>
    </div>
  );
};

CalendarWeekPicker.propTypes = {
  callbackSelection: PropTypes.func.isRequired,
};

export default CalendarWeekPicker;
